// Import react elements
import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

// Import context
import { AuthProvider } from "global/context/AuthContext";
import { UserProvider } from "global/context/UserContext";
import { ThemeProvider } from "global/context/ThemeContext";
import { ToastProvider } from "./global/context/ToastProvider";
import { AccountLastUpdatedProvider } from "global/context/AccountLastUpdatedContext";
import { useCart } from "global/hooks/useCart";

import ProtectedRoute from "global/components/ProtectedRoute";
import Layout from "global/components/layout/Layout"; // Sidebar & Header included

import ActivateAccount from "pages/login/hooks/useActivateNewAccount";

// Import hooks
import { useIsDesktop } from "global/hooks/useIsDesktop";

// Import components
import ToastContainer from "./global/components/ToastContainer";
import { DesktopOnlyOverlay } from "global/components/layout/DesktopOnlyOverlay";

// Import pages
import LandingPage from "pages/landing_page/LandingPage";
import Login from "pages/login/LoginPage";
import LandingPolicyPage from "pages/landing_page/policies/LandingPolicyPage";
import Dashboard from "pages/dashboard/DashboardPage";
import Orders from "pages/orders/OrdersPage";
import BalanceAndTransactions from "pages/balance_and_transactions/BalanceAndTransactionsPage";
import Images from "pages/images/ImagesPage";
import PodDesigns from "pages/POD_designs/PodDesignsPage";
//import PodDesignDetailsPage from "pages/POD_designs/PodDesignDetailsPage";
import PlansPage from "pages/plans/PlansPage";

// Paypal response handler pages
import PayPalBillingSuccess from "pages/account/components/PayPalBillingSuccess";
import PayPalBillingFail from "pages/account/components/PayPalBillingFail";
import PayPalMerchantSuccess from "pages/account/components/PayPalMerchantSuccess";
import PayPalMerchantFail from "pages/account/components/PayPalMerchantFail";

import Account from "pages/account/AccountPage";
import Connections from "pages/connections/ConnectionsPage";
import AiImageGeneration from "pages/ai_image_generation/AiImageGeneration";
import CustomProducts from "pages/custom_products/CustomProductsPage";
import ManageCustomProducts from "pages/custom_products/manage_custom_products/ManageCustomProductsPage";
import ManageMMMsPage from "pages/custom_products/manage_MMMs/ManageMMMsPage";

import TermsAndConditions from "pages/terms_and_conditions/TermsAndConditionsPage";




const CartLoader = () => {

    // Keep the cart module loaded once so it is available anywhere in the portal.
    useCart();

    return null;

};


const App = () => {

    // Get the current viewport permission state
    const {
        canViewApplication,
        hasLoadedAppConfig,
    } = useIsDesktop();



    // Avoid briefly showing the desktop-only message before configuration loads
    if (!hasLoadedAppConfig) {

        return null;

    }



    // Show the desktop-only message when the viewport is unsupported
    /*
    if (!canViewApplication) {

        return <DesktopOnlyOverlay />;

    }
    */

    

    return (

        <ToastProvider maxVisible={5}>

            <AuthProvider>

                <UserProvider>

                    <AccountLastUpdatedProvider>

                    <ThemeProvider>

                        <CartLoader />

                        <Router>

                            <Routes>
                                {/* Public Route: Landing / Login Pages */}
                                <Route path="/landing" element={<LandingPage />} />
                                {/* All login-related pages share the same Login component */}
                                <Route path="/login" element={<Login />} />
                                <Route path="/register" element={<Login />} />
                                <Route path="/reset-password" element={<Login />} />
                                <Route path="/update-password" element={<Login />} />
                                <Route path="/update_email" element={<Login />} />
                                <Route path="/activate_account" element={<ActivateAccount />} />
                                
                                <Route path="/privacy-policy" element={<LandingPolicyPage />} />
                                

                                {/* Catch-All for Root Path */}
                                {/* Only authorised users can access these */}
                                <Route path="/" element={
                                    <ProtectedRoute>
                                        <Layout />
                                    </ProtectedRoute>
                                }>
                                    <Route index element={<Navigate to="/dashboard" />} />
                                    <Route path="dashboard" element={<Dashboard />} />
                                    <Route path="orders" element={<Orders />} />
                                    <Route path="transactions" element={<BalanceAndTransactions />} />
                                    <Route path="image-bucket" element={<Images />} />
                                    
                                    {/* List of POD designs */}
                                    <Route
                                        path="/designs"
                                        element={<PodDesigns designOrTemplate="design" />}
                                    />

                                    {/* List of POD templates */}
                                    <Route
                                        path="/designs/templates"
                                        element={<PodDesigns designOrTemplate="template" />}
                                    />

                                    {/* Single POD design details page */}
                                    {/* Details page: /pod/designs/design-1 
                                    <Route
                                        path="/designs/:podDesignSlug"
                                        element={<PodDesignDetailsPage />}
                                    />
                                    */}

                                    {/* Single POD template details page */}
                                    {/* Details page: /pod/templates/template-1 
                                    <Route
                                        path="/designs/templates/:podDesignSlug"
                                        element={<PodDesignDetailsPage />}
                                    />
                                    */}

                                    <Route path="ai-studio" element={<AiImageGeneration />} />
                                    <Route path="account" element={<Account />} />

                                    <Route path="plans" element={<PlansPage />} />

                                    <Route path="stores" element={<Connections />} />
                                    <Route path="custom-products" element={<CustomProducts />} />
                                    <Route path="custom-products/manage-products" element={<ManageCustomProducts />} />
                                    <Route path="custom-products/MMMs" element={<ManageMMMsPage />} />

                                    {/* PayPal response handlers added by Ben */}
                                    <Route path="account/paypal/billing-success" element={<PayPalBillingSuccess />} />
                                    <Route path="account/paypal/billing-fail" element={<PayPalBillingFail />} />
                                    <Route path="account/paypal/merchant-success" element={<PayPalMerchantSuccess />} />
                                    <Route path="account/paypal/merchant-fail" element={<PayPalMerchantFail />} />

                                    {/* T&Cs */}
                                    <Route path="terms-and-conditions" element={<TermsAndConditions documentType="terms" />} />

                                    {/* Privacy Notice */}
                                    <Route path="privacy-notice" element={<TermsAndConditions documentType="privacy" />} />

                                    {/* Optional: inner 404 to see if this part is reached */}
                                    <Route
                                        path="*"
                                        element={<div>Inner 404 from / route</div>}
                                    />
                                </Route>

                                {/* Global 404 to see which Routes block is complaining */}
                                <Route
                                    path="*"
                                    element={<div>Outer 404 no route matched</div>}
                                />
                            </Routes>
                        </Router>

                    </ThemeProvider>

                    </AccountLastUpdatedProvider>

                </UserProvider>

            </AuthProvider>

        <ToastContainer />

        </ToastProvider>

    );
    
};

export default App;
